import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import {
  ApiService,
  PlatformOrganizationBillingMonthItem,
  PlatformOrganizationBillingSummaryResponse,
} from '../../shared/services/api.service';

@Component({
  selector: 'app-platform-organization-billing',
  templateUrl: './platform-organization-billing.component.html',
  styleUrls: ['./platform-organization-billing.component.css'],
})
export class PlatformOrganizationBillingComponent implements OnInit {
  loading = false;
  saving = false;
  orgId = '';
  data: PlatformOrganizationBillingSummaryResponse | null = null;
  readonly settleForm: FormGroup;
  readonly displayedColumns: string[] = [
    'month',
    'teamMembers',
    'chargeCount',
    'billed',
    'settled',
    'outstanding',
    'dueAt',
    'status',
  ];

  private readonly zarFormatter = new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  constructor(
    private readonly apiService: ApiService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly fb: FormBuilder,
    private readonly snackBar: MatSnackBar
  ) {
    this.settleForm = this.fb.group({
      amount: ['', [Validators.required, Validators.min(0.01)]],
      note: [''],
    });
  }

  ngOnInit(): void {
    this.orgId = String(this.route.snapshot.paramMap.get('orgId') || '').trim();
    if (!this.orgId) {
      this.snackBar.open('Missing organization id', 'Close', { duration: 5000 });
      void this.router.navigate(['/platform-admin']);
      return;
    }
    this.load();
  }

  load(): void {
    this.loading = true;
    this.apiService.getPlatformOrganizationBillingSummary(this.orgId).subscribe({
      next: (response) => {
        this.data = response;
        const defaultAmount = Math.max(0, Number(response?.summary?.totalDueCents || 0)) / 100;
        this.settleForm.patchValue({
          amount: defaultAmount > 0 ? defaultAmount.toFixed(2) : '',
          note: '',
        });
      },
      error: (error) => {
        const message = error?.error?.error?.message || 'Failed to load billing summary';
        this.snackBar.open(message, 'Close', { duration: 6000 });
      },
      complete: () => {
        this.loading = false;
      },
    });
  }

  goBack(): void {
    void this.router.navigate(['/platform-admin']);
  }

  settle(): void {
    if (this.settleForm.invalid || this.saving || !this.data) return;
    const rawAmount = Number(this.settleForm.get('amount')?.value);
    if (!Number.isFinite(rawAmount) || rawAmount <= 0) {
      this.snackBar.open('Enter a valid paid amount', 'Close', { duration: 4000 });
      return;
    }
    const amountCents = Math.round(rawAmount * 100);
    const note = String(this.settleForm.get('note')?.value || '').trim();
    this.saving = true;
    this.apiService
      .settlePlatformOrganizationInvoice(this.orgId, {
        amountCents,
        note: note || undefined,
      })
      .subscribe({
        next: () => {
          this.snackBar.open('Settlement recorded', 'Close', { duration: 4000 });
          this.load();
        },
        error: (error) => {
          const message = error?.error?.error?.message || 'Failed to record settlement';
          this.snackBar.open(message, 'Close', { duration: 6000 });
        },
        complete: () => {
          this.saving = false;
        },
      });
  }

  formatZarFromCents(value: number | null | undefined): string {
    const cents = Number(value || 0);
    return this.zarFormatter.format(cents / 100);
  }

  formatDateTime(value: string | null | undefined): string {
    const raw = String(value || '').trim();
    if (!raw) return '-';
    const parsed = new Date(raw);
    if (Number.isNaN(parsed.getTime())) return raw;
    return new Intl.DateTimeFormat('sv-SE', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    }).format(parsed);
  }

  monthLabel(monthKey: string): string {
    const normalized = String(monthKey || '').trim();
    if (!/^\d{4}-\d{2}$/.test(normalized)) return normalized || '-';
    const [year, month] = normalized.split('-').map((part) => Number(part));
    const parsed = new Date(Date.UTC(year, month - 1, 1));
    return new Intl.DateTimeFormat('en-ZA', {
      timeZone: 'Africa/Johannesburg',
      year: 'numeric',
      month: 'long',
    }).format(parsed);
  }

  rowStatusClass(row: PlatformOrganizationBillingMonthItem): string {
    const status = String(row?.status || '').toUpperCase();
    if (status === 'OVERDUE') return 'status-overdue';
    if (status === 'PARTIAL') return 'status-partial';
    if (status === 'PAID') return 'status-paid';
    return 'status-open';
  }

  isInvoiceOrg(): boolean {
    return this.data?.organization?.billingPaymentType === 'INVOICE';
  }

  isCardOrg(): boolean {
    return this.data?.organization?.billingPaymentType === 'CARD';
  }
}
